import {useMutation} from '@tanstack/react-query';
import dayjs from 'dayjs';
import {useMemo} from 'react';

import {createEventsMutationOptions} from '@/services/eventService';
import {graphqlClient} from '@/services/graphqlClient';
import type {
  Event,
  GetEventsWithDateRangeInput,
  Resident,
  User,
} from '@/services/graphqlHooks';
import {
  useGetAllCurrentResidentsForInputQuery,
  useGetAllUsersForInputQuery,
  useGetEventsByDateRangeQuery,
} from '@/services/graphqlHooks';
import type {EventWithResidentAndUser} from '@/shared/events/utils/eventsColumns';
import {eventsDateRangeInput} from '@/shared/events/utils/eventsHelpers';
import {TIME_FORMATS} from '@/utils/timeFormats';

/***********************
 * Event Helper Hooks
 ***********************/

type UseEventsOptions = {
  input?: GetEventsWithDateRangeInput;
  enabled?: boolean;
};

const belongsToNonCurrentResident = (
  event: Event,
  currentResident?: Resident
) => !!event.pairingId && !currentResident;

export const useEvents = (options?: UseEventsOptions) => {
  const {input, enabled = true} = options || {};
  const requestedRange = eventsDateRangeInput(input);

  const {
    data: residentsData,
    isFetching: isResidentsFetching,
    isLoading: isResidentsLoading,
  } = useGetAllCurrentResidentsForInputQuery(graphqlClient, undefined, {
    enabled,
    staleTime: Infinity,
  });
  const {
    data: usersData,
    isFetching: isUsersFetching,
    isLoading: isUsersLoading,
  } = useGetAllUsersForInputQuery(graphqlClient, undefined, {
    enabled,
    staleTime: Infinity,
  });

  // `Event.date` is a full UTC timestamp, but the backend bounds the query
  // against UTC midnight, not the browser's local midnight. Pad the fetch by
  // a day on each side and re-filter by the event's *local* day below, so an
  // event just after local midnight isn't attributed to the wrong day.
  const eventsByDateRange = useGetEventsByDateRangeQuery(
    graphqlClient,
    {
      input: {
        startDate: dayjs(requestedRange.startDate)
          .subtract(1, 'day')
          .format(TIME_FORMATS.awsDate),
        endDate: dayjs(requestedRange.endDate)
          .add(1, 'day')
          .format(TIME_FORMATS.awsDate),
      },
    },
    {
      enabled: enabled && !!residentsData && !!usersData,
    }
  );

  const sortedEvents = useMemo(() => {
    const events = (eventsByDateRange?.data?.getEventsByDateRange ||
      []) as Event[];
    const residents = (residentsData?.getAllCurrentResidents ||
      []) as Resident[];
    const users = (usersData?.getAllUsers || []) as User[];

    const eventsInRange = events.filter(event => {
      const eventDate = dayjs(event.date).format(TIME_FORMATS.awsDate);
      return (
        !dayjs(eventDate).isBefore(dayjs(requestedRange.startDate), 'day') &&
        !dayjs(eventDate).isAfter(dayjs(requestedRange.endDate), 'day')
      );
    });

    const eventsWithUserAndResident =
      eventsInRange.flatMap<EventWithResidentAndUser>(event => {
        const resident = residents.find(
          resident => resident.id === event.pairingId
        );
        const user = users.find(user => user.userName === event.createdBy);

        return belongsToNonCurrentResident(event, resident)
          ? []
          : [{...event, resident, user}];
      });

    return eventsWithUserAndResident.sort(
      (x, y) => new Date(y.date).getTime() - new Date(x.date).getTime()
    );
  }, [
    eventsByDateRange?.data,
    residentsData,
    usersData,
    requestedRange.startDate,
    requestedRange.endDate,
  ]);

  const isLoading =
    enabled &&
    (isResidentsLoading || isUsersLoading || eventsByDateRange.isLoading);

  const isFetching =
    enabled &&
    (isResidentsFetching || isUsersFetching || eventsByDateRange.isFetching);

  return {
    ...eventsByDateRange,
    isFetching,
    isLoading,
    events: sortedEvents,
  };
};

export const useCreateEvents = () => {
  return useMutation(createEventsMutationOptions);
};
